import React, { useState, useRef, useEffect } from 'react'
import { BiChevronDown } from 'react-icons/bi'
import { AiOutlineSearch } from 'react-icons/ai'
import { MdAddBox } from 'react-icons/md'
import { HiCheck } from 'react-icons/hi'
import { IoClose } from 'react-icons/io5'
import CustomerSelectorItem from '../CustomerSelectorItem'
import { useCurrent } from '../../hooks'
import TagListItem from './TagListItem'

const MwSelectorMulti = ({
  label,
  _data,
  dataType,
  onClickNew,
  setSelectedItem,
  selectedItem,
  initalValue = 'Choose',
  withLabel = true,
  withAddNew = true,
  className,
  disabled = false,
}) => {
  const [inputValue, setInputValue] = useState('')
  const { currentLangId } = useCurrent()
  const [open, setOpen] = useState(false)
  const isMultiSelect = dataType === 'salesman'
  const buttonRef = useRef(null)
  const dropdownRef = useRef(null)
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 })

  const onSelectedHandler = (item) => {
    if (selectedItem.some((obj) => obj.value === item.value)) return
    setSelectedItem([...selectedItem, { ...item, label: item.label }])
    setOpen(false)
    setInputValue('')
  }

  const onSelectedHandlerCustomer = (item) => {
    setSelectedItem((prev) => {
      if (isMultiSelect) {
        if (!Array.isArray(prev)) return [item]
        if (prev.some((i) => i.value === item.value)) return prev
        return [...prev, item]
      }
      return item
    })
    setOpen(false)
    setInputValue('')
  }

  const isSelected = (item) =>
    Array.isArray(selectedItem) && selectedItem.some((s) => s.value === item.value)

  const dataList =
    _data &&
    _data.map((item) => {
      if (dataType === 'customer' || dataType === 'salesman') {
        return (
          <CustomerSelectorItem
            key={item._id}
            item={item}
            setSelectedItem={onSelectedHandlerCustomer}
            setOpen={setOpen}
            setInputValue={setInputValue}
            inputValue={inputValue}
          />
        )
      }

      if (dataType === 'labelValue') {
        const visible =
          item.lang === currentLangId
            ? item?.label?.toLowerCase().startsWith(inputValue)
            : item?.label?.toLowerCase().includes(inputValue.toLowerCase())

        return (
          <div
            key={item.value}
            className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer transition-colors
              ${visible ? 'flex' : 'hidden'}
              ${isSelected(item)
                ? 'text-indigo-600 dark:text-indigo-400 font-medium bg-indigo-50 dark:bg-indigo-950/30'
                : 'text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700'
              }`}
            onClick={() => onSelectedHandler(item)}
          >
            <span className={`w-4 h-4 flex-shrink-0 ${isSelected(item) ? 'opacity-100' : 'opacity-0'}`}>
              <HiCheck className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
            </span>
            {item.label}
          </div>
        )
      }
    })

  const removeItem = (tag) => {
    setSelectedItem(selectedItem.filter((t) => t.label !== tag))
  }

  useEffect(() => {
    if (disabled && open) setOpen(false)
  }, [disabled, open])

  useEffect(() => {
    if (open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      setDropdownPosition({ top: rect.bottom + 4, left: rect.left, width: rect.width })
    }
  }, [open])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        open &&
        buttonRef.current && !buttonRef.current.contains(event.target) &&
        dropdownRef.current && !dropdownRef.current.contains(event.target)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  return (
    <div className={`relative w-full ${className}`}>
      {withLabel && label && (
        <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
          {label}
        </label>
      )}

      <div
        ref={buttonRef}
        onClick={disabled ? undefined : () => setOpen(!open)}
        className={`
          flex items-center gap-2 min-h-[38px] px-3 py-1.5 w-full
          bg-white dark:bg-slate-800
          border dark:border-slate-600
          rounded-lg
          transition-all duration-150
          ${open
            ? 'border-indigo-400 ring-2 ring-indigo-100 dark:ring-indigo-900/40'
            : 'border-slate-200 hover:border-slate-300 dark:hover:border-slate-500'
          }
          ${disabled ? 'opacity-50 cursor-not-allowed bg-slate-50 dark:bg-slate-900' : 'cursor-pointer'}
        `}
      >
        <div className="flex-1 flex flex-wrap gap-1 min-h-[22px] items-center">
          {selectedItem?.length > 0 ? (
            selectedItem.map((el, idx) => (
              <span
                key={`${el.value}-${idx}`}
                className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 text-xs font-medium rounded-full"
              >
                {el.label}
                {!disabled && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); removeItem(el.label) }}
                    className="text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-200 transition-colors"
                  >
                    <IoClose className="w-3 h-3" />
                  </button>
                )}
              </span>
            ))
          ) : (
            <span className="text-sm text-slate-400 dark:text-slate-500">{initalValue}</span>
          )}
        </div>

        <BiChevronDown
          className={`flex-shrink-0 text-slate-400 dark:text-slate-500 text-lg transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </div>

      {open && (
        <div
          ref={dropdownRef}
          className="fixed z-[9999] bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-600 shadow-lg overflow-hidden"
          style={{ top: `${dropdownPosition.top}px`, left: `${dropdownPosition.left}px`, width: `${dropdownPosition.width}px` }}
        >
          <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/80">
            <AiOutlineSearch className="text-slate-400 dark:text-slate-500 text-base flex-shrink-0" />
            <input
              type="text"
              autoFocus
              placeholder={`Search ${label?.toLowerCase() || ''}…`}
              className="flex-1 text-sm bg-transparent border-none outline-none text-slate-700 dark:text-slate-200 placeholder-slate-400"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value.toLowerCase())}
            />
            {withAddNew && onClickNew && (
              <button
                type="button"
                onClick={() => { onClickNew(); setOpen(false) }}
                className="text-indigo-500 hover:text-indigo-700 dark:text-indigo-400 transition-colors"
                title="Add new"
              >
                <MdAddBox className="text-xl" />
              </button>
            )}
          </div>

          <div className="max-h-56 overflow-y-auto divide-y divide-slate-50 dark:divide-slate-700/50">
            {dataList}
          </div>
        </div>
      )}
    </div>
  )
}

export default MwSelectorMulti